import { useState } from "react";
import { UserPlus, Copy, Trash2, Loader2, ChevronDown, ChevronUp, Mail, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { openWhatsAppJournalInvite } from "../lib/whatsapp";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

export const InviteCard = ({ trip, onRefresh }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [showList, setShowList] = useState(false);
  const links = (trip.magic_links || []).filter((l) => !l.revoked);

  const invite = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Vul een naam in (bv. Oma, Tom & Lies)");
    if (!email.includes("@")) return toast.error("Vul een geldig e-mailadres in");
    setBusy(true);
    try {
      const { data } = await api.post(`/trips/${trip.id}/invite-journal`, { name: name.trim(), email: email.trim() });
      const url = `${window.location.origin}/journal/${data.token}`;
      try { await navigator.clipboard.writeText(url); } catch (err) { console.warn("Klembord niet beschikbaar", err); }
      toast.success(`Uitnodiging verstuurd naar ${data.email}!`, { description: "De persoonlijke link is ook gekopieerd — stuur 'm gerust zelf door via WhatsApp." });
      setName("");
      setEmail("");
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const inviteWhatsApp = async () => {
    if (!name.trim()) return toast.error("Vul een naam in (bv. Oma, Tom & Lies)");
    setBusy(true);
    try {
      const { data } = await api.post(`/trips/${trip.id}/magic-links`, { name: name.trim() });
      openWhatsAppJournalInvite(trip, data.name, data.token);
      toast.success(`WhatsApp-uitnodiging voor ${data.name} klaargezet!`);
      setName("");
      setEmail("");
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async (l) => {
    const url = `${window.location.origin}/journal/${l.token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(`Link voor ${l.name} gekopieerd!`, { description: url });
    } catch {
      toast.info(`Link voor ${l.name}`, { description: url, duration: 10000 });
    }
  };

  const revoke = async (token) => {
    await api.delete(`/trips/${trip.id}/magic-links/${token}`);
    toast.success("Toegang ingetrokken");
    onRefresh?.();
  };

  return (
    <div data-testid="invite-card" className="rounded-2xl p-5 border border-[#B5838D]/30 bg-gradient-to-br from-[#B5838D]/15 via-[#B5838D]/5 to-transparent">
      <div className="flex items-start gap-3 mb-3">
        <span className="w-10 h-10 rounded-full bg-[#B5838D] text-white flex items-center justify-center shrink-0 shadow">
          <UserPlus className="w-5 h-5" />
        </span>
        <div>
          <p className="font-heading font-bold text-base leading-tight">Nodig familie & vrienden uit</p>
          <p className="text-xs text-muted-foreground mt-0.5">Zij volgen je dagverslagen, foto's en video's en kunnen per dag reageren — geen account nodig.</p>
        </div>
      </div>
      <form onSubmit={invite} className="space-y-2">
        <div className="flex flex-col sm:flex-row gap-2">
          <Input data-testid="invite-name-input" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Naam (bv. Oma)" className="rounded-xl h-10 sm:w-40 bg-background" />
          <Input data-testid="invite-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="E-mailadres (alleen nodig voor e-mail)" className="rounded-xl h-10 flex-1 bg-background" />
        </div>
        <div className="flex gap-2">
          <Button data-testid="invite-send-button" type="submit" disabled={busy}
            className="rounded-full bg-[#B5838D] hover:bg-[#8E5D67] h-10 flex-1">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Mail className="w-4 h-4 mr-1.5" /> Verstuur e-mail</>}
          </Button>
          <Button data-testid="invite-whatsapp-button" type="button" onClick={inviteWhatsApp} disabled={busy}
            className="rounded-full bg-[#25D366] hover:bg-[#1fb457] text-white h-10 flex-1">
            <MessageCircle className="w-4 h-4 mr-1.5" /> Via WhatsApp
          </Button>
        </div>
      </form>
      {links.length > 0 && (
        <div className="mt-3">
          <button data-testid="invite-links-toggle" onClick={() => setShowList((s) => !s)}
            className="text-xs font-semibold text-[#8E5D67] flex items-center gap-1 hover:underline">
            {links.length} {links.length === 1 ? "persoon" : "personen"} uitgenodigd
            {showList ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
          {showList && (
            <div className="mt-2 space-y-1.5">
              {links.map((l) => (
                <div key={l.token} data-testid={`invite-link-row-${l.token}`} className="bg-background/70 rounded-xl px-3 py-2 flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">{l.name}</p>
                    {l.email && <p className="text-xs text-muted-foreground truncate">{l.email}</p>}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button data-testid={`invite-whatsapp-${l.token}`} onClick={() => openWhatsAppJournalInvite(trip, l.name, l.token)} title="Stuur via WhatsApp"
                      className="p-1.5 rounded-full hover:bg-[#25D366]/10 text-[#25D366] transition-[background-color] duration-200">
                      <MessageCircle className="w-4 h-4" />
                    </button>
                    <button data-testid={`invite-copy-${l.token}`} onClick={() => copyLink(l)} title="Kopieer link"
                      className="p-1.5 rounded-full hover:bg-secondary text-primary transition-[background-color] duration-200">
                      <Copy className="w-4 h-4" />
                    </button>
                    <button data-testid={`invite-revoke-${l.token}`} onClick={() => revoke(l.token)} title="Toegang intrekken"
                      className="p-1.5 rounded-full hover:bg-red-50 text-red-500 transition-[background-color] duration-200">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
